import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, UserPlus, Crown, Shield, Edit3, Eye,
  Trash2, Mail, Clock, CheckCircle2, XCircle, Loader2,
  AlertTriangle, ChevronDown, RefreshCw, ArrowRightLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { getErrorMessage } from '@/utils/errorHelpers';
import { getSeatLimit, formatLimit } from '@/permissions/tierLimits';

// Seat limit is now dynamic via tierLimits.js — this constant is kept as a fallback only
const DEFAULT_MAX_TEAM_SIZE = 10;

const roleConfig = {
  owner: {
    label: 'Owner',
    icon: Crown,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    description: 'Full control including ownership transfer'
  },
  full_access: {
    label: 'Full Access',
    icon: Shield,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    description: 'Team & billing management'
  },
  editor: {
    label: 'Editor',
    icon: Edit3,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    description: 'Add & edit emission entries'
  },
  viewer: {
    label: 'View Only',
    icon: Eye,
    color: 'text-slate-600',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    description: 'View dashboard & export reports'
  }
};

const TeamPage = () => {
  const navigate = useNavigate();
  const {
    currentOrg,
    userRole,
    user: authUser,
    isAdmin,
    effectiveTier,
    canPerform
  } = useOutletContext();

  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);
  const [teamStats, setTeamStats] = useState({ total_count: 0, max_size: DEFAULT_MAX_TEAM_SIZE, can_invite: false });
  // local user internal ID needed for some comparison
  const [currentUserId, setCurrentUserId] = useState(null);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!authUser) {
      navigate('/login');
      return;
    }

    const fetchInternalUserId = async () => {
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('supabase_user_id', authUser.id)
        .single();
      if (userData) {
        setCurrentUserId(userData.id);
      }
    };

    fetchInternalUserId();
  }, [authUser, navigate]);

  useEffect(() => {
    if (currentOrg?.id && authUser && currentUserId) {
      fetchTeam(currentOrg.id, effectiveTier);
    }
  }, [currentOrg?.id, effectiveTier, authUser, currentUserId]);

  const fetchTeam = async (orgId, tier = 'demo') => {
    const maxTeamSize = getSeatLimit(tier);
    setLoading(true);
    try {
      // Get organisation to check owner
      const { data: org } = await supabase
        .from('organisations')
        .select('owner_id')
        .eq('id', orgId)
        .single();

      if (!org) {
        toast.error('Organisation not found');
        setLoading(false);
        return;
      }

      // Get owner info
      const { data: ownerData } = await supabase
        .from('users')
        .select('id, email, name, is_admin, is_superadmin')
        .eq('id', org.owner_id)
        .single();

      // Get all members (without join)
      const { data: members } = await supabase
        .from('organisation_members')
        .select('id, user_id, role, invitation_status, invited_email, invited_at, accepted_at')
        .eq('organisation_id', orgId);

      // Get user info for members with user_id
      const memberUserIds = (members || [])
        .filter(m => m.user_id)
        .map(m => m.user_id);

      let usersMap = {};
      if (memberUserIds.length > 0) {
        const { data: usersData } = await supabase
          .from('users')
          .select('id, email, name, is_admin, is_superadmin')
          .in('id', memberUserIds);

        usersMap = (usersData || []).reduce((acc, u) => {
          acc[u.id] = u;
          return acc;
        }, {});
      }

      // Build team list
      const teamList = [];

      // Add owner first
      if (ownerData) {
        teamList.push({
          id: `owner-${ownerData.id}`,
          user_id: ownerData.id,
          email: ownerData.email,
          name: ownerData.name,
          role: 'owner',
          is_owner: true,
          invitation_status: 'accepted',
          accepted_at: null,
          is_admin: ownerData.is_admin,
          is_superadmin: ownerData.is_superadmin
        });
      }

      // Add members
      (members || []).forEach(member => {
        // Skip if this is the owner (already added)
        if (member.user_id === org.owner_id) return;

        const userInfo = usersMap[member.user_id];
        teamList.push({
          id: member.id,
          user_id: member.user_id,
          email: userInfo?.email || member.invited_email,
          name: userInfo?.name,
          role: member.role,
          is_owner: false,
          invitation_status: member.invitation_status,
          invited_at: member.invited_at,
          accepted_at: member.accepted_at,
          is_admin: userInfo?.is_admin || false,
          is_superadmin: userInfo?.is_superadmin || false
        });
      });

      setTeam(teamList);

      // Calculate stats
      const billableCount = teamList.filter(member => !member.is_admin && !member.is_superadmin).length;

      // Use canPerform for invite gating
      const canInvite = canPerform('SETTINGS_TEAM_MGMT');

      setTeamStats({
        total_count: billableCount,
        max_size: maxTeamSize,
        can_invite: (canInvite || isAdmin) && billableCount < maxTeamSize,
        tier: tier
      });
    } catch (error) {
      console.error('Error fetching team:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };


  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !currentOrg) {
      toast.error('Please enter an email address');
      return;
    }

    setInviting(true);
    try {
      // Check if email already exists in team
      const existingMember = team.find(m => m.email?.toLowerCase() === inviteEmail.trim().toLowerCase());
      if (existingMember) {
        toast.error('This email is already a team member');
        setInviting(false);
        return;
      }

      // Check team size
      if (team.length >= teamStats.max_size) {
        toast.error(`Team is at maximum capacity (${teamStats.max_size} members)`);
        setInviting(false);
        return;
      }

      // Check if user exists
      const { data: existingUser } = await supabase
        .from('users')
        .select('id')
        .eq('email', inviteEmail.trim().toLowerCase())
        .single();

      // Create invitation
      const { data, error } = await supabase
        .from('organisation_members')
        .insert({
          organisation_id: currentOrg.id,
          user_id: existingUser?.id || null,
          role: inviteRole,
          invitation_status: 'pending',
          invited_email: inviteEmail.trim().toLowerCase(),
          invited_at: new Date().toISOString(),
          invitation_token: crypto.randomUUID()
        })
        .select()
        .single();

      if (error) throw error;

      // Add audit log
      await supabase.from('audit_logs').insert({
        organisation_id: currentOrg.id,
        resource_type: 'team_member',
        resource_id: data.id,
        action: 'invite',
        description: `Invited ${inviteEmail} as ${roleConfig[inviteRole]?.label || inviteRole}`
      });

      toast.success(`Invitation sent to ${inviteEmail}`);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('viewer');

      // Refresh team list
      await fetchTeam(currentOrg.id, effectiveTier);
    } catch (error) {
      console.error('Error inviting member:', error);
      toast.error(getErrorMessage(error, 'Failed to send invitation'));
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (newRole) => {
    if (!selectedMember || !currentOrg) return;

    setUpdating(true);
    try {
      const { error } = await supabase
        .from('organisation_members')
        .update({ role: newRole })
        .eq('id', selectedMember.id);

      if (error) throw error;

      // Add audit log
      await supabase.from('audit_logs').insert({
        organisation_id: currentOrg.id,
        resource_type: 'team_member',
        resource_id: selectedMember.id,
        action: 'update_role',
        description: `Changed role for ${selectedMember.email} to ${roleConfig[newRole]?.label || newRole}`
      });

      toast.success(`Role updated to ${roleConfig[newRole]?.label || newRole}`);
      setShowRoleModal(false);
      setSelectedMember(null);

      // Refresh team list
      await fetchTeam(currentOrg.id, effectiveTier);
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error(getErrorMessage(error, 'Failed to update role'));
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!selectedMember || !currentOrg) return;

    setUpdating(true);
    try {
      const { error } = await supabase
        .from('organisation_members')
        .delete()
        .eq('id', selectedMember.id);

      if (error) throw error;

      // Add audit log
      await supabase.from('audit_logs').insert({
        organisation_id: currentOrg.id,
        resource_type: 'team_member',
        resource_id: selectedMember.id,
        action: 'remove',
        description: `Removed ${selectedMember.email} from team`
      });

      toast.success('Team member removed');
      setShowRemoveModal(false);
      setSelectedMember(null);

      // Refresh team list
      await fetchTeam(currentOrg.id, effectiveTier);
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error(getErrorMessage(error, 'Failed to remove member'));
    } finally {
      setUpdating(false);
    }
  };

  const handleTransferOwnership = async () => {
    if (!selectedMember || !currentOrg || !currentUserId) return;

    setUpdating(true);
    try {
      // Update organisation owner
      const { error: orgError } = await supabase
        .from('organisations')
        .update({ owner_id: selectedMember.user_id })
        .eq('id', currentOrg.id);

      if (orgError) throw orgError;

      // Update the new owner's membership to full_access (or remove it since they're now owner)
      await supabase
        .from('organisation_members')
        .delete()
        .eq('organisation_id', currentOrg.id)
        .eq('user_id', selectedMember.user_id);

      // Add the old owner as full_access member
      await supabase
        .from('organisation_members')
        .insert({
          organisation_id: currentOrg.id,
          user_id: currentUserId,
          role: 'full_access',
          invitation_status: 'accepted',
          accepted_at: new Date().toISOString()
        });

      // Add audit log
      await supabase.from('audit_logs').insert({
        organisation_id: currentOrg.id,
        resource_type: 'organisation',
        resource_id: currentOrg.id,
        action: 'transfer_ownership',
        description: `Ownership transferred to ${selectedMember.name || selectedMember.email}`
      });

      toast.success(`Ownership transferred to ${selectedMember.name || selectedMember.email}`);
      setShowTransferModal(false);
      setSelectedMember(null);

      // Refresh everything
      await fetchTeam(currentOrg.id, effectiveTier);
    } catch (error) {
      console.error('Error transferring ownership:', error);
      toast.error(getErrorMessage(error, 'Failed to transfer ownership'));
    } finally {
      setUpdating(false);
    }
  };

  const refreshTeam = async () => {
    if (!currentOrg) return;
    await fetchTeam(currentOrg.id, effectiveTier);
  };

  const canManageTeam = canPerform('SETTINGS_TEAM_MGMT') || isAdmin;
  const isOwner = userRole?.role === 'owner';

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]">
      {/* Page Header */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Users className="w-7 h-7 mr-3 text-[#74A57F]" />
              Team Management
            </h1>
            <p className="text-slate-600 mt-1">
              {currentOrg?.name || 'Loading...'} • Manage your team members and roles
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={refreshTeam}
              className="text-slate-600"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            {teamStats.can_invite && (
              <Button
                onClick={() => setShowInviteModal(true)}
                className="bg-[#003B5C] hover:bg-[#002A42] text-white"
                data-testid="invite-member-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Member
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-12">
        {/* Tier Seats Alert */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-[#003B5C]" />
                <span className="text-lg font-semibold text-[#003B5C]">{teamStats.total_count}</span>
                <span className="text-slate-500">/ {teamStats.max_size} seats used</span>
              </div>
              <div className="h-4 w-px bg-slate-200"></div>
              <div className="flex items-center space-x-2">
                <div className="w-32 bg-slate-100 rounded-full h-2">
                  <div
                    className="bg-[#74A57F] h-2 rounded-full transition-all"
                    style={{ width: `${(teamStats.total_count / teamStats.max_size) * 100}%` }}
                  ></div>
                </div>
                <span className="text-xs text-slate-500">
                  {teamStats.max_size - teamStats.total_count} seats available
                  {teamStats.tier && teamStats.tier !== 'pro' && teamStats.total_count >= teamStats.max_size && (
                    <span className="ml-2 text-purple-600 font-medium cursor-pointer hover:underline" onClick={() => navigate('/pricing')}>
                      Upgrade for more
                    </span>
                  )}
                </span>
              </div>
            </div>
            {userRole && (
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-slate-500">Your role:</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleConfig[userRole.role]?.bg} ${roleConfig[userRole.role]?.color}`}>
                  {roleConfig[userRole.role]?.label || userRole.role}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Team List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#003B5C]" />
          </div>
        ) : (
          <div className="space-y-3">
            {team.map((member, index) => {
              const config = roleConfig[member.role] || roleConfig.viewer;
              const RoleIcon = config.icon;
              const isPending = member.invitation_status === 'pending';

              return (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-white rounded-xl border ${isPending ? 'border-dashed border-slate-300' : 'border-slate-200'} p-4 hover:shadow-md transition-shadow`}
                  data-testid={`team-member-${member.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {/* Avatar */}
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isPending ? 'bg-slate-100' : config.bg}`}>
                        {isPending ? (
                          <Clock className="w-5 h-5 text-slate-400" />
                        ) : member.is_owner ? (
                          <Crown className="w-5 h-5 text-amber-600" />
                        ) : (
                          <span className="text-lg font-semibold text-slate-600">
                            {(member.name || member.email || '?')[0].toUpperCase()}
                          </span>
                        )}
                      </div>

                      {/* Info */}
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold text-slate-800">
                            {member.name || member.email.split('@')[0]}
                          </span>
                          {member.is_owner && (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                              Owner
                            </span>
                          )}
                          {(member.is_admin || member.is_superadmin) && (
                            <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium flex items-center">
                              <Shield className="w-3 h-3 mr-1" />
                              Lucent Sky Admin (Excluded from seat limit)
                            </span>
                          )}
                          {isPending && (
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium flex items-center">
                              <Mail className="w-3 h-3 mr-1" />
                              Pending
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-3 text-sm text-slate-500">
                          <span>{member.email}</span>
                          {member.accepted_at && (
                            <>
                              <span>•</span>
                              <span>Joined {new Date(member.accepted_at).toLocaleDateString()}</span>
                            </>
                          )}
                          {isPending && member.invited_at && (
                            <>
                              <span>•</span>
                              <span>Invited {new Date(member.invited_at).toLocaleDateString()}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Role Badge & Actions */}
                    <div className="flex items-center space-x-3">
                      <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg ${config.bg} ${config.border} border`}>
                        <RoleIcon className={`w-4 h-4 ${config.color}`} />
                        <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
                      </div>

                      {canManageTeam && !member.is_owner && (
                        <div className="flex items-center space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedMember(member); setShowRoleModal(true); }}
                            className="text-slate-500 hover:text-[#003B5C]"
                            title="Change Role"
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedMember(member); setShowRemoveModal(true); }}
                            className="text-slate-500 hover:text-red-600"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      {isOwner && !member.is_owner && member.user_id && member.invitation_status === 'accepted' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedMember(member); setShowTransferModal(true); }}
                          className="text-slate-500 hover:text-amber-600"
                          title="Transfer Ownership"
                        >
                          <ArrowRightLeft className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {team.length === 0 && (
              <div className="text-center py-16 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No team members yet</p>
                {teamStats.can_invite && (
                  <Button
                    onClick={() => setShowInviteModal(true)}
                    className="mt-4 bg-[#003B5C] hover:bg-[#002A42] text-white"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invite Your First Member
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Role Legend */}
        <div className="mt-8 bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-800 mb-4">Role Permissions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(roleConfig).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <div key={key} className={`p-4 rounded-xl ${config.bg} ${config.border} border`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon className={`w-5 h-5 ${config.color}`} />
                    <span className={`font-medium ${config.color}`}>{config.label}</span>
                  </div>
                  <p className="text-xs text-slate-500">{config.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full"
          >
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-[#003B5C]">Invite Team Member</h2>
              <p className="text-sm text-slate-500 mt-1">They'll receive an email invitation to join</p>
            </div>
            <form onSubmit={handleInvite} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  required
                  data-testid="invite-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                <div className="space-y-2">
                  {['viewer', 'editor', 'full_access'].map((role) => {
                    const config = roleConfig[role];
                    const Icon = config.icon;
                    return (
                      <label
                        key={role}
                        className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${inviteRole === role
                          ? `${config.bg} ${config.border}`
                          : 'border-slate-200 hover:bg-slate-50'
                          }`}
                      >
                        <input
                          type="radio"
                          name="role"
                          value={role}
                          checked={inviteRole === role}
                          onChange={(e) => setInviteRole(e.target.value)}
                          className="sr-only"
                        />
                        <Icon className={`w-5 h-5 mr-3 ${inviteRole === role ? config.color : 'text-slate-400'}`} />
                        <div>
                          <div className={`font-medium ${inviteRole === role ? config.color : 'text-slate-700'}`}>
                            {config.label}
                          </div>
                          <div className="text-xs text-slate-500">{config.description}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowInviteModal(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={inviting}
                  className="bg-[#003B5C] hover:bg-[#002A42] text-white"
                  data-testid="send-invite-btn"
                >
                  {inviting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Invitation
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Change Role Modal */}
      {showRoleModal && selectedMember && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full"
          >
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-[#003B5C]">Change Role</h2>
              <p className="text-sm text-slate-500 mt-1">
                Update role for {selectedMember.name || selectedMember.email}
              </p>
            </div>
            <div className="p-6 space-y-2">
              {['viewer', 'editor', 'full_access'].map((role) => {
                const config = roleConfig[role];
                const Icon = config.icon;
                const isCurrentRole = selectedMember.role === role;
                return (
                  <button
                    key={role}
                    onClick={() => handleUpdateRole(role)}
                    disabled={updating || isCurrentRole}
                    className={`w-full flex items-center p-3 rounded-lg border transition-colors ${isCurrentRole
                      ? `${config.bg} ${config.border} cursor-default`
                      : 'border-slate-200 hover:bg-slate-50'
                      }`}
                  >
                    <Icon className={`w-5 h-5 mr-3 ${config.color}`} />
                    <div className="text-left flex-1">
                      <div className={`font-medium ${config.color}`}>{config.label}</div>
                      <div className="text-xs text-slate-500">{config.description}</div>
                    </div>
                    {isCurrentRole && (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="p-6 border-t border-slate-200">
              <Button variant="outline" onClick={() => setShowRoleModal(false)} className="w-full">
                Cancel
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Remove Member Modal */}
      {showRemoveModal && selectedMember && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Remove Team Member</h2>
                <p className="text-sm text-slate-500">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-slate-600 mb-6">
              Are you sure you want to remove <strong>{selectedMember.name || selectedMember.email}</strong> from this organisation?
            </p>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={() => setShowRemoveModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleRemoveMember}
                disabled={updating}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {updating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Remove'
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Transfer Ownership Modal */}
      {showTransferModal && selectedMember && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <ArrowRightLeft className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Transfer Ownership</h2>
                <p className="text-sm text-slate-500">This is a significant action</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-amber-800">
                <strong>Warning:</strong> You will lose owner privileges and become a Full Access member.
                Only the new owner will be able to delete the organisation or transfer ownership again.
              </p>
            </div>
            <p className="text-slate-600 mb-6">
              Transfer ownership of <strong>{currentOrg?.name}</strong> to <strong>{selectedMember.name || selectedMember.email}</strong>?
            </p>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={() => setShowTransferModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleTransferOwnership}
                disabled={updating}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                {updating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Transfer Ownership'
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TeamPage;
